import React from 'react'
import { Card, CardContent, CardMedia, Typography, Chip, Box, Grid, Divider, IconButton, Collapse, Button } from '@mui/material'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import PeopleIcon from '@mui/icons-material/People'
import TimerIcon from '@mui/icons-material/Timer'
import CookingIcon from '@mui/icons-material/LocalDining'
import NutritionIcon from '@mui/icons-material/Fastfood'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import StarIcon from '@mui/icons-material/Star'

interface RecipeListComponentProps {
    mode: 'list' | 'delete';
    _id: string;
    recipeName: string;
    recipeType: string;
    recipeTrimester: string;
    recipePreparationTime: string;
    recipeCookTime: string;
    recipeServes: number;
    recipeTags: any;
    recipeMethod: string;
    recipeIngredients: any;
    recipeImage: string;
    recipeCarbs: number;
    recipeProtein: number;
    recipeFats: number;
    recipeCalories: number;
    recipeCalcium: number;
    recipeVitaminA: number,
    recipeVitaminAUnit: string,
    recipeVitaminB6: number,
    recipeVitaminB6Unit: string,
    recipeVitaminB12: string,
    recipeVitaminB12Unit: string,
    recipeVitaminC: number,
    recipeVitaminCUnit: string,
    recipeVitaminD: number,
    recipeVitaminDUnit: string,
    recipeVitaminE: number,
    recipeVitaminEUnit: string,
    recipeVitaminK: number,
    recipeVitaminKUnit: string,
    recipeCholesterol: number,
    recipeCholesterolUnit: string,
    recipeCholine: number,
    recipeCholineUnit: string,
    recipeFiber: number,
    recipeFiberUnit: string,
    recipeFolicAcid: number,
    recipeFolicAcidUnit: string,
    recipeIodine: number,
    recipeIodineUnit: string,
    recipeIron: number,
    recipeIronUnit: string,
    recipeMagnesium: number,
    recipeMagnesiumUnit: string,
    recipePotassium: number,
    recipePotassiumUnit: string,
    recipeSaturatedFat: number,
    recipeSaturatedFatUnit: string,
    blogReferences: any;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
    isPriority: boolean;
    isPremium: boolean;
    open: boolean;
    setOpen: (open: boolean) => void;
    selectedRecipeTitle?: string;
    setSelectedRecipeTitle?: (selectedBlogTitle: string) => void;
    selectedRecipeId?: string;
    setSelectedRecipeId?: (selectedBlogId: string) => void;
}

export default function RecipeListComponent({
    mode,
    _id,
    recipeName,
    recipeType,
    recipeTrimester,
    recipePreparationTime,
    recipeCookTime,
    recipeServes,
    recipeTags,
    recipeMethod,
    recipeIngredients,
    recipeImage,
    recipeCarbs,
    recipeProtein,
    recipeFats,
    recipeCalories,
    recipeCalcium,
    recipeVitaminA,
    recipeVitaminAUnit,
    recipeVitaminB6,
    recipeVitaminB6Unit,
    recipeVitaminB12,
    recipeVitaminB12Unit,
    recipeVitaminC,
    recipeVitaminCUnit,
    recipeVitaminD,
    recipeVitaminDUnit,
    recipeVitaminE,
    recipeVitaminEUnit,
    recipeVitaminK,
    recipeVitaminKUnit,
    recipeCholesterol,
    recipeCholesterolUnit,
    recipeCholine,
    recipeCholineUnit,
    recipeFiber,
    recipeFiberUnit,
    recipeFolicAcid,
    recipeFolicAcidUnit,
    recipeIodine,
    recipeIodineUnit,
    recipeIron,
    recipeIronUnit,
    recipeMagnesium,
    recipeMagnesiumUnit,
    recipePotassium,
    recipePotassiumUnit,
    recipeSaturatedFat,
    recipeSaturatedFatUnit,
    blogReferences,
    createdAt,
    updatedAt,
    isDeleted,
    isPriority,
    isPremium,
    open,
    setOpen,
    selectedRecipeTitle,
    setSelectedRecipeTitle,
    selectedRecipeId,
    setSelectedRecipeId,
}: RecipeListComponentProps) {
    const [expanded, setExpanded] = React.useState(false);

    const returnDate = (date: number) => {
        const newDate = new Date(date * 1000);
        return newDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const handleOpen = () => {
        setSelectedRecipeTitle!(recipeName);
        setSelectedRecipeId!(_id);
        setOpen(true)
    };

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    const [showFullMethod, setShowFullMethod] = React.useState(false);
    const [showFullIngredients, setShowFullIngredients] = React.useState(false);

    const totalTime = Number(recipePreparationTime) + Number(recipeCookTime);

    // Parse recipe method into numbered steps
    const parseMethodSteps = (method: string) => {
        if (!method) return [];
        
        // Check if method contains HTML tags
        const hasHtmlTags = /<[^>]*>/g.test(method);
        
        if (hasHtmlTags) {
            // If it's HTML, split by common HTML block elements or line breaks
            return method.split(/(?:<\/(?:p|div|li|h[1-6]|br\s*\/?)>|<br\s*\/?>|\n)/i)
                .map(line => line.replace(/<[^>]*>/g, '').trim()) // Remove HTML tags for step text
                .filter(line => line.length > 0)
                .map((line, index) => {
                    const cleanLine = line.replace(/^\d+[.)\-\s]*/, '').trim();
                    return cleanLine;
                });
        } else {
            // Original logic for plain text
            return method.split('\n')
                .map(line => line.trim())
                .filter(line => line.length > 0)
                .map((line, index) => {
                    const cleanLine = line.replace(/^\d+[.)\-\s]*/, '').trim();
                    return cleanLine;
                });
        }
    };

    // Check if method contains HTML
    const isHtmlMethod = /<[^>]*>/g.test(recipeMethod);

    // Check if first ingredient contains HTML
    const isHtmlIngredient = recipeIngredients && recipeIngredients.length > 0 && /<[^>]*>/g.test(recipeIngredients[0]);

    // Function to render method content (HTML or plain text)
    const renderMethodContent = (content: string) => {
        if (isHtmlMethod) {
            return <div dangerouslySetInnerHTML={{ __html: content }} />;
        }
        return <span>{content}</span>;
    };

    // Function to render ingredient content (HTML or plain text)
    const renderIngredientContent = (ingredient: string, index: number) => {
        // Only render HTML for the first ingredient if it contains HTML
        if (index === 0 && isHtmlIngredient) {
            return <div dangerouslySetInnerHTML={{ __html: ingredient }} />;
        }
        return <span>{ingredient}</span>;
    };

    const methodSteps = parseMethodSteps(recipeMethod);

    // Organize nutrition data
    const basicNutrition = [
        { label: 'Calories', value: recipeCalories, unit: 'kcal' },
        { label: 'Carbs', value: recipeCarbs, unit: 'g' },
        { label: 'Protein', value: recipeProtein, unit: 'g' },
        { label: 'Fats', value: recipeFats, unit: 'g' },
        { label: 'Calcium', value: recipeCalcium, unit: 'mg' },
    ];

    const vitamins = [
        { label: 'Vitamin A', value: recipeVitaminA, unit: recipeVitaminAUnit },
        { label: 'Vitamin B6', value: recipeVitaminB6, unit: recipeVitaminB6Unit },
        { label: 'Vitamin B12', value: recipeVitaminB12, unit: recipeVitaminB12Unit },
        { label: 'Vitamin C', value: recipeVitaminC, unit: recipeVitaminCUnit },
        { label: 'Vitamin D', value: recipeVitaminD, unit: recipeVitaminDUnit },
        { label: 'Vitamin E', value: recipeVitaminE, unit: recipeVitaminEUnit },
        { label: 'Vitamin K', value: recipeVitaminK, unit: recipeVitaminKUnit },
    ];

    const minerals = [
        { label: 'Cholesterol', value: recipeCholesterol, unit: recipeCholesterolUnit },
        { label: 'Choline', value: recipeCholine, unit: recipeCholineUnit },
        { label: 'Fiber', value: recipeFiber, unit: recipeFiberUnit },
        { label: 'Folate', value: recipeFolicAcid, unit: recipeFolicAcidUnit },
        { label: 'Iodine', value: recipeIodine, unit: recipeIodineUnit },
        { label: 'Iron', value: recipeIron, unit: recipeIronUnit },
        { label: 'Magnesium', value: recipeMagnesium, unit: recipeMagnesiumUnit },
        { label: 'Potassium', value: recipePotassium, unit: recipePotassiumUnit },
        { label: 'Saturated Fat', value: recipeSaturatedFat, unit: recipeSaturatedFatUnit },
    ];

    return (
        <Card
            sx={{
                minWidth: 900,
                width: 900,
                margin: '16px auto',
                boxShadow: isDeleted ? '0 4px 20px rgba(239, 68, 68, 0.3)' : '0 4px 20px rgba(0,0,0,0.1)',
                border: isDeleted ? '2px solid #ef4444' : isPriority ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                },
                cursor: mode === 'delete' ? 'pointer' : 'default'
            }}
            onClick={mode === 'delete' ? handleOpen : undefined}
        >
            {/* Main Image */}
            <CardMedia sx={{ height: 300, position: 'relative' }}>
                <LazyLoadImage
                    src={recipeImage}
                    alt={recipeName}
                    effect="blur"
                    style={{
                        width: '100%',
                        height: '300px',
                        objectFit: 'cover'
                    }}
                />

                {/* Status Badges */}
                <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 1, flexDirection: 'column' }}>
                    {isPriority && (
                        <Chip
                            icon={<PriorityHighIcon />}
                            label="Priority"
                            size="small"
                            sx={{
                                backgroundColor: '#f59e0b',
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                        />
                    )}
                    {isDeleted && (
                        <Chip
                            label="Deleted"
                            size="small"
                            sx={{
                                backgroundColor: '#ef4444',
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                        />
                    )}
                    {isPremium && (
                        <Chip
                            icon={<StarIcon />}
                            label="Premium"
                            size="small"
                            sx={{
                                backgroundColor: '#8b5cf6',
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                        />
                    )}
                    <Chip
                        label={`Trimester ${recipeTrimester}`}
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(59, 130, 246, 0.9)',
                            color: 'white',
                            fontWeight: 'bold'
                        }}
                    />
                </Box>
            </CardMedia>

            <CardContent sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
            }}>
                {/* Main Content Area */}
                <Box sx={{ flex: 1 }}>
                    {/* Title and Recipe Info */}
                    <Typography variant="h4" component="h1" gutterBottom sx={{
                        fontWeight: 'bold',
                        color: isDeleted ? '#ef4444' : 'inherit',
                        mb: 3
                    }}>
                        <RestaurantIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '2rem' }} />
                        {recipeName}
                    </Typography>

                    {/* Quick Stats Row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TimerIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                                {recipePreparationTime} min prep
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CookingIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                                {recipeCookTime} min cook
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTimeIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                                {totalTime} min total
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PeopleIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                                Serves {recipeServes}
                            </Typography>
                        </Box>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                        <strong>Type:</strong> {recipeType}
                    </Typography>

                    {/* Recipe Tags */}
                    {recipeTags && recipeTags.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <LocalOfferIcon fontSize="small" color="primary" />
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                    Tags ({recipeTags.length})
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {recipeTags.map((tag: any, index: number) => (
                                    <Chip
                                        key={index}
                                        label={tag}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Basic Nutrition Overview */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <NutritionIcon color="primary" />
                            Nutrition Facts
                        </Typography>
                        <Grid container spacing={2}>
                            {basicNutrition.map((item, index) => (
                                <Grid item xs={6} sm={4} md={2.4} key={index}>
                                    <Box sx={{
                                        textAlign: 'center',
                                        p: 2,
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '8px',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                            {item.value}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {item.unit}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: '500', mt: 0.5 }}>
                                            {item.label}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>

                    {/* Ingredients Preview */}
                    {recipeIngredients && recipeIngredients.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Ingredients ({recipeIngredients.length})
                            </Typography>
                            <Box sx={{
                                backgroundColor: '#f8fafc',
                                padding: '12px',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                maxHeight: showFullIngredients ? 'none' : '200px',
                                overflow: 'hidden'
                            }}>
                                {recipeIngredients.slice(0, showFullIngredients ? recipeIngredients.length : 4).map((ingredient: any, index: number) => (
                                    <Typography 
                                        key={index} 
                                        variant="body2" 
                                        sx={{ 
                                            mb: 1.5, 
                                            lineHeight: 1.6,
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1
                                        }}
                                    >
                                        <Box component="span" sx={{ 
                                            fontWeight: 'bold', 
                                            color: 'primary.main',
                                            minWidth: '20px',
                                            flexShrink: 0
                                        }}>
                                            {index + 1}.
                                        </Box>
                                        <Box component="span">
                                            {renderIngredientContent(ingredient, index)}
                                        </Box>
                                    </Typography>
                                ))}
                                {!showFullIngredients && recipeIngredients.length > 4 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
                                        +{recipeIngredients.length - 4} more ingredients...
                                    </Typography>
                                )}
                            </Box>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => setShowFullIngredients(!showFullIngredients)}
                                startIcon={showFullIngredients ? <ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} /> : <ExpandMoreIcon />}
                                sx={{ 
                                    mt: 2,
                                    borderRadius: '20px',
                                    textTransform: 'none',
                                    fontWeight: '500',
                                    px: 3,
                                    py: 1,
                                    border: '2px solid',
                                    borderColor: 'primary.main',
                                    backgroundColor: 'transparent',
                                    color: 'primary.main',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                                    },
                                    '& .MuiButton-startIcon': {
                                        transition: 'transform 0.3s ease'
                                    }
                                }}
                            >
                                {showFullIngredients ? 'Show Less' : `Show All ${recipeIngredients.length} Ingredients`}
                            </Button>
                        </Box>
                    )}

                    {/* Method Preview */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Method {!isHtmlMethod && `(${methodSteps.length} steps)`}
                        </Typography>
                        <Box sx={{
                            backgroundColor: '#f8fafc',
                            padding: '12px',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            maxHeight: showFullMethod ? 'none' : '200px',
                            overflow: 'hidden'
                        }}>
                            {isHtmlMethod ? (
                                // Render HTML directly for HTML content
                                <div 
                                    dangerouslySetInnerHTML={{ 
                                        __html: showFullMethod ? recipeMethod : recipeMethod.substring(0, 300) + (recipeMethod.length > 300 ? '...' : '')
                                    }} 
                                    style={{ lineHeight: 1.6 }}
                                />
                            ) : (
                                // Render as numbered steps for plain text
                                <>
                                    {methodSteps.slice(0, showFullMethod ? methodSteps.length : 3).map((step, index) => (
                                        <Typography 
                                            key={index}
                                            variant="body2" 
                                            sx={{ 
                                                mb: 1.5, 
                                                lineHeight: 1.6,
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 1
                                            }}
                                        >
                                            <Box component="span" sx={{ 
                                                fontWeight: 'bold', 
                                                color: 'primary.main',
                                                minWidth: '20px',
                                                flexShrink: 0
                                            }}>
                                                {index + 1}.
                                            </Box>
                                            <Box component="span">
                                                {step.replace(/^\d+\.\s*/, '')}
                                            </Box>
                                        </Typography>
                                    ))}
                                    {!showFullMethod && methodSteps.length > 3 && (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
                                            +{methodSteps.length - 3} more steps...
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Box>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setShowFullMethod(!showFullMethod)}
                            startIcon={showFullMethod ? <ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} /> : <ExpandMoreIcon />}
                            sx={{ 
                                mt: 2,
                                borderRadius: '20px',
                                textTransform: 'none',
                                fontWeight: '500',
                                px: 3,
                                py: 1,
                                border: '2px solid',
                                borderColor: 'primary.main',
                                backgroundColor: 'transparent',
                                color: 'primary.main',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                                },
                                '& .MuiButton-startIcon': {
                                    transition: 'transform 0.3s ease'
                                }
                            }}
                        >
                            {showFullMethod ? 'Show Less' : (isHtmlMethod ? 'Show Full Method' : `Show All ${methodSteps.length} Steps`)}
                        </Button>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Metadata */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Created: {returnDate(createdAt)}
                        </Typography>
                        {updatedAt !== createdAt && (
                            <Typography variant="body2" color="text.secondary">
                                Updated: {returnDate(updatedAt)}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Bottom Section - Expand Button */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <IconButton
                        onClick={handleExpandClick}
                        aria-expanded={expanded}
                        aria-label="show more"
                        sx={{
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s'
                        }}
                    >
                        <ExpandMoreIcon />
                    </IconButton>
                </Box>

                {/* Expanded Content */}
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Detailed Information
                        </Typography>

                        {/* Complete Ingredients List */}
                        {recipeIngredients && recipeIngredients.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    All Ingredients ({recipeIngredients.length})
                                </Typography>
                                <Box sx={{
                                    backgroundColor: '#f8fafc',
                                    padding: '16px',
                                    borderRadius: '8px',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    {recipeIngredients.map((ingredient: any, index: number) => (
                                        <Box 
                                            key={index} 
                                            sx={{ 
                                                mb: 2,
                                                pb: index < recipeIngredients.length - 1 ? 1.5 : 0,
                                                borderBottom: index < recipeIngredients.length - 1 ? '1px solid #e2e8f0' : 'none'
                                            }}
                                        >
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    lineHeight: 1.6,
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 1.5
                                                }}
                                            >
                                                <Box 
                                                    component="span" 
                                                    sx={{ 
                                                        fontWeight: 'bold', 
                                                        minWidth: '30px',
                                                        flexShrink: 0,
                                                        backgroundColor: 'success.main',
                                                        color: 'white',
                                                        borderRadius: '50%',
                                                        width: '24px',
                                                        height: '24px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    {index + 1}
                                                </Box>
                                                <Box component="span" sx={{ flex: 1 }}>
                                                    {renderIngredientContent(ingredient, index)}
                                                </Box>
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Full Method */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Complete Method {!isHtmlMethod && `(${methodSteps.length} steps)`}
                            </Typography>
                            <Box sx={{
                                backgroundColor: '#f8fafc',
                                padding: '16px',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0'
                            }}>
                                {isHtmlMethod ? (
                                    // Render HTML directly for HTML content
                                    <div 
                                        dangerouslySetInnerHTML={{ __html: recipeMethod }} 
                                        style={{ lineHeight: 1.6 }}
                                    />
                                ) : (
                                    // Render as numbered steps for plain text
                                    methodSteps.map((step, index) => (
                                        <Box 
                                            key={index} 
                                            sx={{ 
                                                mb: 2.5,
                                                pb: index < methodSteps.length - 1 ? 2 : 0,
                                                borderBottom: index < methodSteps.length - 1 ? '1px solid #e2e8f0' : 'none'
                                            }}
                                        >
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    lineHeight: 1.6,
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 1.5
                                                }}
                                            >
                                                <Box 
                                                    component="span" 
                                                    sx={{ 
                                                        fontWeight: 'bold', 
                                                        minWidth: '30px',
                                                        flexShrink: 0,
                                                        backgroundColor: 'primary.main',
                                                        color: 'white',
                                                        borderRadius: '50%',
                                                        width: '24px',
                                                        height: '24px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    {index + 1}
                                                </Box>
                                                <Box component="span" sx={{ flex: 1 }}>
                                                    {step.replace(/^\d+\.\s*/, '')}
                                                </Box>
                                            </Typography>
                                        </Box>
                                    ))
                                )}
                            </Box>
                        </Box>

                        {/* Detailed Nutrition */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Complete Nutrition Profile
                            </Typography>

                            {/* Vitamins */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
                                Vitamins
                            </Typography>
                            <Grid container spacing={2}>
                                {vitamins.filter(v => v.value && String(v.value) !== '').map((vitamin, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Box sx={{
                                            backgroundColor: '#fef3c7',
                                            padding: '8px 12px',
                                            borderRadius: '6px',
                                            border: '1px solid #fbbf24'
                                        }}>
                                            <Typography variant="body2">
                                                <strong>{vitamin.label}:</strong> {vitamin.value} {vitamin.unit}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Minerals & Others */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
                                Minerals & Other Nutrients
                            </Typography>
                            <Grid container spacing={2}>
                                {minerals.filter(m => m.value && String(m.value) !== '').map((mineral, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Box sx={{
                                            backgroundColor: '#dbeafe',
                                            padding: '8px 12px',
                                            borderRadius: '6px',
                                            border: '1px solid #3b82f6'
                                        }}>
                                            <Typography variant="body2">
                                                <strong>{mineral.label}:</strong> {mineral.value} {mineral.unit}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>

                        {/* Recipe Metadata */}
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Recipe Details
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Recipe ID:</strong> {_id}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Type:</strong> {recipeType}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Trimester:</strong> {recipeTrimester}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Status:</strong> {isDeleted ? 'Deleted' : 'Active'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Priority:</strong> {isPriority ? 'High' : 'Normal'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Premium:</strong> {isPremium ? 'Yes' : 'No'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Serves:</strong> {recipeServes} people
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Total Time:</strong> {totalTime} minutes
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
}
